// 콘텐츠 / 버전 관리 — 05-admin §5: variant 상세 + lineage + 모더레이션/격리/purge.
//   GET /v1/variants/{id} (01 §3.D), GET /v1/variants/{id}/lineage,
//   POST /v1/admin/variants/{id}/moderate, POST /v1/admin/variants/{id}/purge (csam_reviewer),
//   GET /v1/admin/variants/{id}/evidence-preview (격리물 감사 미리보기, READ마다 audit).
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import { navigate } from "../core/router.js";
import type { LineageNode, VariantDetail } from "../models.js";

function isLegalHold(v: VariantDetail): boolean {
  return v.legal_hold === true || v.legal_hold === 1;
}

function statusBadge(s?: string): HTMLElement {
  if (!s) return h("span", { class: "badge" }, "—");
  const cls =
    s === "published" ? "ok" : s === "quarantined" || s === "removed" || s === "rejected" || s === "failed" ? "danger" : "warn";
  return h("span", { class: `badge ${cls}` }, s);
}

function labelBadges(v: VariantDetail): HTMLElement {
  const l = v.label;
  if (!l) return h("span", { class: "muted small" }, "라벨 없음");
  const tags: HTMLElement[] = [];
  if (l.ai_generated) tags.push(h("span", { class: "badge" }, "AI 생성"));
  if (l.ai_edited) tags.push(h("span", { class: "badge" }, "AI 편집"));
  if (l.deepfake) tags.push(h("span", { class: "badge danger" }, "딥페이크"));
  if (l.audio_watermark_id) tags.push(h("span", { class: "badge" }, "오디오 워터마크"));
  return tags.length ? h("span", { class: "row wrap" }, ...tags) : h("span", { class: "muted small" }, "라벨 없음");
}

// 모더레이션/격리 액션 다이얼로그 (사유 필수 → 감사 로그, §3.2).
function moderateDialog(v: VariantDetail, onDone: () => void): void {
  const reasonField = h("input", { class: "field", placeholder: "사유 (필수, 감사 로그 기록)" }) as HTMLInputElement;
  const legalField = h("input", { class: "field", placeholder: "legal_basis (선택)" }) as HTMLInputElement;
  const status = h("p", { class: "small muted" }, "");
  const backdrop = h("div", { class: "dialog-backdrop" });
  const close = (): void => backdrop.remove();

  async function moderate(action: "removed" | "rejected" | "quarantined"): Promise<void> {
    const reason = reasonField.value.trim();
    if (!reason) {
      status.textContent = "사유를 입력하세요.";
      status.className = "small error";
      return;
    }
    status.textContent = "처리 중…";
    status.className = "small muted";
    try {
      // cascade(descendant Cut/Short take-down + 매니페스트 거부, §3.2)는 서버가 적용.
      await api.post(`/v1/admin/variants/${v.id}/moderate`, {
        action,
        reason,
        legal_basis: legalField.value.trim() || undefined,
      });
      close();
      onDone();
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (moderate 미구현)." : `실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  backdrop.appendChild(
    h(
      "div",
      { class: "dialog wide" },
      h("h3", {}, "상태 전이 / 모더레이션"),
      h("p", { class: "muted small" }, `대상 variant: ${v.id} (현재 ${v.status ?? "?"})`),
      h(
        "p",
        { class: "muted small" },
        "removed/rejected/quarantined 전이 시 서버가 cascade(coverage에 포함하는 모든 Cut/Short/descendant take-down + 매니페스트 거부, §3.2/§5.3)를 적용합니다.",
      ),
      reasonField,
      legalField,
      status,
      h(
        "div",
        { class: "row wrap" },
        h("button", { class: "btn danger", onclick: () => void moderate("removed") }, "테이크다운 (removed)"),
        h("button", { class: "btn", onclick: () => void moderate("rejected") }, "거부 (rejected)"),
        h("button", { class: "btn danger", onclick: () => void moderate("quarantined") }, "CSAM 격리 (quarantined)"),
        h("button", { class: "btn secondary", onclick: close }, "닫기"),
      ),
    ),
  );
  document.body.appendChild(backdrop);
}

// CSAM purge 다이얼로그 — 영구삭제, 사유·legal_basis 필수 → audit (§4.2).
function purgeDialog(v: VariantDetail, onDone: () => void): void {
  const reasonField = h("input", { class: "field", placeholder: "사유 (필수)" }) as HTMLInputElement;
  const legalField = h("input", {
    class: "field",
    placeholder: "legal_basis (필수, 예: 성폭력처벌법 14조의2)",
  }) as HTMLInputElement;
  const confirmField = h("input", { class: "field", placeholder: 'PURGE 입력하여 확인' }) as HTMLInputElement;
  const status = h("p", { class: "small muted" }, "");
  const backdrop = h("div", { class: "dialog-backdrop" });
  const close = (): void => backdrop.remove();

  async function doPurge(): Promise<void> {
    const reason = reasonField.value.trim();
    const legal = legalField.value.trim();
    if (!reason || !legal) {
      status.textContent = "사유와 legal_basis는 필수입니다.";
      status.className = "small error";
      return;
    }
    if (confirmField.value.trim() !== "PURGE") {
      status.textContent = '확인을 위해 "PURGE"를 입력하세요.';
      status.className = "small error";
      return;
    }
    status.textContent = "영구삭제 중…";
    status.className = "small muted";
    try {
      // POST /v1/admin/variants/{id}/purge (moderator + csam_reviewer). legal_hold 활성 시 409.
      await api.post(`/v1/admin/variants/${v.id}/purge`, { reason, legal_basis: legal });
      close();
      onDone();
    } catch (e) {
      if (isNotReady(e)) {
        status.textContent = "엔드포인트 준비중 (purge 미구현).";
      } else if (e instanceof Error && /LEGAL_HOLD|409/.test(errMsg(e))) {
        status.textContent = "legal_hold가 설정되어 purge가 거부되었습니다 (409 LEGAL_HOLD — legal-hold > purge).";
      } else {
        status.textContent = `purge 실패: ${errMsg(e)}`;
      }
      status.className = "small error";
    }
  }

  backdrop.appendChild(
    h(
      "div",
      { class: "dialog wide" },
      h("h3", {}, "CSAM 영구삭제 (purge)"),
      h(
        "p",
        { class: "small error" },
        "되돌릴 수 없는 R2/Stream 객체 영구삭제입니다. csam_reviewer capability 보유자 전용. legal_hold가 걸린 객체는 거부됩니다(409). 격리 보존(증거 패키지) 필요 시 purge하지 마세요.",
      ),
      reasonField,
      legalField,
      confirmField,
      status,
      h(
        "div",
        { class: "row wrap" },
        h("button", { class: "btn danger", onclick: () => void doPurge() }, "영구삭제 실행"),
        h("button", { class: "btn secondary", onclick: close }, "취소"),
      ),
    ),
  );
  document.body.appendChild(backdrop);
}

async function evidencePreview(v: VariantDetail): Promise<void> {
  try {
    // 격리물 전용 감사 미리보기 — 매 호출이 audit_log 기록(§4.2). 초단위 만료 토큰.
    const res = await api.get<{ url?: string; preview_url?: string }>(`/v1/admin/variants/${v.id}/evidence-preview`);
    const url = res.url ?? res.preview_url;
    if (url) window.open(url, "_blank", "noopener");
    else window.alert("미리보기 토큰을 받았으나 URL이 없습니다.");
  } catch (e) {
    window.alert(
      isNotReady(e)
        ? "엔드포인트 준비중 (evidence-preview 미구현)."
        : `미리보기 실패: ${errMsg(e)} (csam_reviewer capability 필요할 수 있음)`,
    );
  }
}

function lineageList(nodes: LineageNode[]): HTMLElement {
  if (nodes.length === 0) return h("div", { class: "state muted" }, "lineage 노드가 없습니다.");
  return h(
    "table",
    { class: "tbl" },
    h("thead", {}, h("tr", {}, h("th", {}, "depth"), h("th", {}, "variant"), h("th", {}, "parent"), h("th", {}, "상태"))),
    h(
      "tbody",
      {},
      ...nodes.map((n) =>
        h(
          "tr",
          {},
          h("td", { class: "muted small" }, n.depth != null ? String(n.depth) : "—"),
          h("td", {}, h("span", { class: "mono small" }, n.id), n.is_original ? h("span", { class: "badge ok" }, "original") : null),
          h("td", { class: "mono small muted" }, n.parent_variant_id ?? "—"),
          h("td", {}, statusBadge(n.status)),
        ),
      ),
    ),
  );
}

async function renderDetail(id: string, root: HTMLElement): Promise<void> {
  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("a", { class: "back", href: "/content", "data-link": "true" }, "← 콘텐츠 조회"),
      h("div", { class: "state muted" }, "variant 상세 불러오는 중…"),
    ),
  );

  let v: VariantDetail;
  try {
    v = await api.get<VariantDetail>(`/v1/variants/${id}`);
  } catch (e) {
    mount(
      root,
      h(
        "div",
        { class: "stack" },
        h("a", { class: "back", href: "/content", "data-link": "true" }, "← 콘텐츠 조회"),
        isNotReady(e)
          ? notReadyState("variant 조회 (/v1/variants/{id}) 가 응답하지 않습니다.")
          : h("div", { class: "state error" }, `variant 상세 실패: ${errMsg(e)}`),
      ),
    );
    return;
  }

  // lineage (best-effort).
  const lineageWrap = h("div", {}, h("div", { class: "state muted" }, "lineage 불러오는 중…"));
  void (async () => {
    try {
      const res = await api.get<{ items?: LineageNode[]; lineage?: LineageNode[] } | LineageNode[]>(
        `/v1/variants/${id}/lineage`,
      );
      const nodes = Array.isArray(res) ? res : (res.items ?? res.lineage ?? []);
      lineageWrap.replaceChildren(lineageList(nodes));
    } catch (e) {
      lineageWrap.replaceChildren(
        isNotReady(e) ? notReadyState("lineage 미구현.") : h("div", { class: "state error" }, `lineage 실패: ${errMsg(e)}`),
      );
    }
  })();

  function render(): void {
    mount(
      root,
      h(
        "div",
        { class: "stack" },
        h("a", { class: "back", href: "/content", "data-link": "true" }, "← 콘텐츠 조회"),
        h(
          "h1",
          {},
          "Variant ",
          h("span", { class: "mono" }, v.id),
          isLegalHold(v) ? h("span", { class: "badge warn" }, "legal_hold") : null,
        ),
        h(
          "section",
          { class: "card" },
          h("h2", {}, "기본 정보"),
          h(
            "dl",
            { class: "kv" },
            h("dt", {}, "상태"),
            h("dd", {}, statusBadge(v.status)),
            h("dt", {}, "구간 [start,end)"),
            h("dd", { class: "mono small" }, `${v.start_ms ?? "?"} – ${v.end_ms ?? "?"} (dur ${v.duration_ms ?? "?"}ms)`),
            h("dt", {}, "author"),
            h("dd", {}, v.author ? "@" + v.author.handle : "—"),
            h("dt", {}, "episode"),
            h("dd", { class: "mono small" }, v.episode_id ?? "—"),
            h("dt", {}, "score"),
            h("dd", {}, v.score != null ? v.score.toFixed(2) : "—"),
            h("dt", {}, "통계"),
            h(
              "dd",
              { class: "muted small" },
              `views ${v.stats?.views ?? 0} · completes ${v.stats?.completes ?? 0} · upvotes ${v.stats?.upvotes ?? 0}`,
            ),
            h("dt", {}, "라벨"),
            h("dd", {}, labelBadges(v)),
          ),
        ),
        h(
          "section",
          { class: "card" },
          h("h2", {}, "조치"),
          h(
            "div",
            { class: "row wrap" },
            h("button", { class: "btn", onclick: () => moderateDialog(v, () => void renderDetail(id, root)) }, "상태 전이 / 모더레이션"),
            h("button", { class: "btn secondary", onclick: () => void evidencePreview(v) }, "격리물 감사 미리보기"),
            h(
              "button",
              {
                class: "btn danger",
                disabled: isLegalHold(v),
                onclick: () => {
                  if (isLegalHold(v)) return;
                  purgeDialog(v, () => void renderDetail(id, root));
                },
              },
              isLegalHold(v) ? "purge (legal_hold로 비활성)" : "CSAM purge",
            ),
          ),
          h(
            "p",
            { class: "muted small" },
            "격리물 미리보기·purge·증거 패키지는 csam_reviewer capability 보유자 전용입니다 (§4.2). 모든 액션은 감사 로그에 기록됩니다.",
          ),
        ),
        h("section", { class: "card" }, h("h2", {}, "Lineage (편집 체인)"), lineageWrap),
      ),
    );
  }

  render();
}

export async function contentDetailView(params: Record<string, string>, root: HTMLElement): Promise<void> {
  await renderDetail(params["id"] ?? "", root);
}

export function contentView(_p: Record<string, string>, root: HTMLElement): void {
  const idField = h("input", { class: "field", placeholder: "variant_id 로 조회" }) as HTMLInputElement;
  const go = (): void => {
    const id = idField.value.trim();
    if (id) navigate(`/content/${encodeURIComponent(id)}`);
  };
  idField.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") go();
  });

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "콘텐츠 / 버전 관리"),
      h(
        "p",
        { class: "muted" },
        "variant 상세·lineage·모더레이션·CSAM 격리/purge (05-admin §5). variant_id로 조회합니다. 테이크다운/격리/purge는 descendant Cut/Short cascade + 매니페스트 무효화를 트리거합니다.",
      ),
      h(
        "section",
        { class: "card" },
        h("div", { class: "row" }, idField, h("button", { class: "btn", onclick: go }, "조회")),
      ),
    ),
  );
}
